import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { userApi, organizationApi, authApi, authorizationApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ConfigViewerCard } from '@/components/config/ConfigViewerCard';
import { ActivationUrlCard } from '@/components/user/ActivationUrlCard';
import { BasicInformationCard } from '@/components/user/BasicInformationCard';
import { CardColumn } from '@/components/layout/CardColumn';
import { UserPasswordCard } from '@/components/user/UserPasswordCard';
import { useAuthStore } from '@/store/auth';
import {
    Role,
    AllInternalRoles,
    AllOrganizationalRoles,
    isInternalRole,
} from '@pillar-portal/database/dist/frontend';
import {
    ActiveRoleAssignmentResponse,
    hasInternalAdminPrivileges,
    getAllRoleAssignments,
    type RoleAssignmentPermissionsView,
    type UserProfileDetailedPublicResponse,
    UserProfileDetailedPublicResponseSchema,
    OrganizationListItem,
    hasAnyInternalRole,
    hasAnyOrganizationalRole,
    SetUserEmailsRequest,
    SetUserPhonesRequest,
    hasCustomerOrganizationCapacity,
} from '@pillar-portal/api-types';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { BackButton } from '@/components/ui/back-button';
import { Status } from '@pillar-portal/database/dist/frontend';
import { OrganizationSelectionCard } from '@/components/profile/organization-selection-card';
import { RoleAssignmentModal } from '@/components/user/RoleAssignmentModal';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { Checkbox } from '@/components/ui/checkbox';
import { SingleUseTokensCard } from '@/components/user/SingleUseTokensCard';
import {
    PolicyEffectivePermissionsCard,
    PolicyPermissionGrantsCard,
} from '@/components/permissions/PolicyPermissionGrantsCard';

// Helper function to determine user type based on internal_roles/roles and role_assignments
export function getUserType(
    user: UserProfileDetailedPublicResponse | undefined
): 'internal' | 'customer' | 'organizational' | undefined {
    if (user === undefined) {
        return undefined;
    }
    const internal: boolean = hasAnyInternalRole(user);
    const organizational: boolean = hasAnyOrganizationalRole(user);
    const roleAssignments: RoleAssignmentPermissionsView[] = getAllRoleAssignments(user);
    const organizationalRoleAssignments: RoleAssignmentPermissionsView[] = roleAssignments.filter(
        (roleAssignment: RoleAssignmentPermissionsView) =>
            roleAssignment.organization_uuid !== null
    );
    const hasCustomerRole: boolean = organizationalRoleAssignments.some(
        (roleAssignment: RoleAssignmentPermissionsView) =>
            hasCustomerOrganizationCapacity(roleAssignment)
    );
    const hasNonCustomerOrganizationalRole: boolean = organizationalRoleAssignments.some(
        (roleAssignment: RoleAssignmentPermissionsView) =>
            !hasCustomerOrganizationCapacity(roleAssignment)
    );
    // Platform rule: internal users may also hold organizational roles.
    if (internal) {
        return 'internal';
    }
    if (hasCustomerRole && !hasNonCustomerOrganizationalRole) {
        return 'customer';
    }
    if (organizational) {
        return 'organizational';
    }
    return undefined;
}

export function getDefaultConfigRoleAssignmentUuid(
    user: UserProfileDetailedPublicResponse | undefined
): string | null {
    if (user === undefined) {
        return null;
    }

    const defaultRoleAssignmentUuid: string | null =
        user.default_role_assignment?.role_assignment_uuid ?? null;
    if (defaultRoleAssignmentUuid !== null) {
        const defaultRoleAssignment: RoleAssignmentPermissionsView | undefined =
            getAllRoleAssignments(
            user
        ).find(
                (roleAssignment: RoleAssignmentPermissionsView) =>
                    roleAssignment.role_assignment_uuid === defaultRoleAssignmentUuid &&
                    roleAssignment.organization_uuid !== null
            );
        return defaultRoleAssignment ? defaultRoleAssignmentUuid : null;
    }

    const organizationalRoleAssignments: RoleAssignmentPermissionsView[] = getAllRoleAssignments(
        user
    ).filter(
        (roleAssignment: RoleAssignmentPermissionsView) => roleAssignment.organization_uuid !== null
    );

    if (organizationalRoleAssignments.length === 1) {
        const onlyRoleAssignment: RoleAssignmentPermissionsView | undefined =
            organizationalRoleAssignments.at(0);
        if (onlyRoleAssignment) {
            return onlyRoleAssignment.role_assignment_uuid;
        }
    }

    return null;
}

export function getOrganizationalRoleAssignmentUuid(
    user: UserProfileDetailedPublicResponse | undefined,
    organizationUuid: string
): string | null {
    if (user === undefined) {
        return null;
    }

    const matchingRoleAssignment: RoleAssignmentPermissionsView | undefined = getAllRoleAssignments(
        user
    ).find(
        (roleAssignment: RoleAssignmentPermissionsView) =>
            roleAssignment.organization_uuid === organizationUuid
    );

    const roleAssignmentUuid: string | undefined = matchingRoleAssignment?.role_assignment_uuid;
    if (roleAssignmentUuid === undefined || roleAssignmentUuid.length === 0) {
        return null;
    }

    return roleAssignmentUuid;
}

export function canViewUserConfigurationCard(
    activeRoleAssignment: ActiveRoleAssignmentResponse | null | undefined
): boolean {
    return hasInternalAdminPrivileges(activeRoleAssignment ?? null);
}

export function buildUserSingleUseTokensQueryKey(
    activeRoleAssignmentId: number | null,
    userUuid: string | undefined
): ReadonlyArray<unknown> {
    return buildRoleScopedQueryKey(activeRoleAssignmentId, ['singleUseTokens', 'user', userUuid]);
}

export async function invalidateUserSingleUseTokensQuery(
    queryClient: Pick<QueryClient, 'invalidateQueries'>,
    activeRoleAssignmentId: number | null,
    userUuid: string | undefined
): Promise<void> {
    await queryClient.invalidateQueries({
        queryKey: buildUserSingleUseTokensQueryKey(activeRoleAssignmentId, userUuid),
    });
}

export interface UserRolesCardProps {
    isAdmin: boolean;
    isDeleted: boolean;
    roleAssignments: readonly RoleAssignmentPermissionsView[];
    canImpersonateByRoleAssignmentUuid: Readonly<Record<string, boolean>> | null;
    onAssignRole: () => void;
    onEditRole: (roleAssignment: {
        role_assignment_uuid: string;
        role: Role;
        organization_uuid: string | null;
    }) => void;
    onRevokeRole: (roleAssignmentUuid: string, role: Role) => void;
    onRestoreRole: (roleAssignment: {
        role: Role;
        organization_uuid: string | null;
    }) => void;
    onImpersonateRole: (
        roleAssignmentUuid: string,
        role: Role,
        organizationName: string | null
    ) => void;
}

export function UserRolesCard({
    isAdmin,
    isDeleted,
    roleAssignments,
    canImpersonateByRoleAssignmentUuid,
    onAssignRole,
    onEditRole,
    onRevokeRole,
    onRestoreRole,
    onImpersonateRole,
}: UserRolesCardProps) {
    const [showDeleted, setShowDeleted] = useState(false);
    if (!isAdmin) {
        return null;
    }

    const hasDeletedRoleAssignments: boolean = roleAssignments.some(
        (roleAssignment) => roleAssignment.status === Status.DELETED
    );
    const visibleRoleAssignments: readonly RoleAssignmentPermissionsView[] = showDeleted
        ? roleAssignments
        : roleAssignments.filter((roleAssignment) => roleAssignment.status !== Status.DELETED);

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Roles</CardTitle>
                <div className="flex gap-2">
                    {hasDeletedRoleAssignments && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowDeleted((currentValue) => !currentValue)}
                        >
                            {showDeleted ? 'Hide deleted' : 'Show deleted'}
                        </Button>
                    )}
                    {!isDeleted && (
                        <Button onClick={onAssignRole} size="sm">
                            Assign Role
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-2">
                    {visibleRoleAssignments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No roles assigned</p>
                    ) : (
                        visibleRoleAssignments.map((roleAssignment) => {
                            const orgName: string | null =
                                roleAssignment.organization_uuid !== null
                                    ? roleAssignment.organization_name
                                    : null;

                            return (
                                <div
                                    key={roleAssignment.role_assignment_uuid}
                                    className="flex items-center justify-between p-3 border rounded"
                                >
                                    <div className="flex items-center gap-2">
                                        <Badge variant="secondary">
                                            {roleAssignment.role}
                                        </Badge>
                                        {roleAssignment.status === Status.DELETED && (
                                            <Badge variant="destructive">Deleted</Badge>
                                        )}
                                        {roleAssignment.status === Status.DISABLED && (
                                            <Badge variant="outline">Disabled</Badge>
                                        )}
                                        {roleAssignment.status === Status.PENDING && (
                                            <Badge variant="outline">Pending</Badge>
                                        )}
                                        {orgName !== null &&
                                            roleAssignment.organization_uuid !== null && (
                                                <span className="text-sm text-muted-foreground">
                                                    @{' '}
                                                    <Link
                                                        to={`/organizations/${roleAssignment.organization_uuid}`}
                                                        className="underline-offset-4 hover:underline"
                                                    >
                                                        {orgName}
                                                    </Link>
                                                </span>
                                            )}
                                    </div>
                                    {!isDeleted && (
                                        <div className="flex gap-2">
                                            {roleAssignment.status === Status.DELETED ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        onRestoreRole({
                                                            role: roleAssignment.role,
                                                            organization_uuid:
                                                                roleAssignment.organization_uuid,
                                                        })
                                                    }
                                                >
                                                    Restore
                                                </Button>
                                            ) : (
                                                <>
                                                    {roleAssignment.role !== 'SUPERADMIN' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() =>
                                                                onEditRole({
                                                                    role_assignment_uuid:
                                                                        roleAssignment.role_assignment_uuid,
                                                                    role: roleAssignment.role,
                                                                    organization_uuid:
                                                                        roleAssignment.organization_uuid,
                                                                })
                                                            }
                                                        >
                                                            Edit
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                            onRevokeRole(
                                                                roleAssignment.role_assignment_uuid,
                                                                roleAssignment.role
                                                            )
                                                        }
                                                    >
                                                        Revoke
                                                    </Button>
                                                    {canImpersonateByRoleAssignmentUuid?.[
                                                        roleAssignment.role_assignment_uuid
                                                    ] === true && (
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            onClick={() => {
                                                                onImpersonateRole(
                                                                    roleAssignment.role_assignment_uuid,
                                                                    roleAssignment.role,
                                                                    orgName
                                                                );
                                                            }}
                                                        >
                                                            Impersonate
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

interface UserPolicyPermissionGrantsSectionProps {
    isAdmin: boolean;
    isDeleted: boolean;
    roleAssignments: RoleAssignmentPermissionsView[];
}

export function UserPolicyPermissionGrantsSection({
    isAdmin,
    isDeleted,
    roleAssignments,
}: UserPolicyPermissionGrantsSectionProps) {
    const organizationScopedRoleAssignments: RoleAssignmentPermissionsView[] = roleAssignments.filter(
        (roleAssignment: RoleAssignmentPermissionsView) =>
            roleAssignment.organization_uuid !== null
    );

    if (!isAdmin || isDeleted || organizationScopedRoleAssignments.length === 0) {
        return null;
    }

    return <PolicyPermissionGrantsCard roleAssignments={organizationScopedRoleAssignments} />;
}

interface UserEffectivePermissionsSectionProps {
    isAdmin: boolean;
    isDeleted: boolean;
    roleAssignments: RoleAssignmentPermissionsView[];
}

export function UserEffectivePermissionsSection({
    isAdmin,
    isDeleted,
    roleAssignments,
}: UserEffectivePermissionsSectionProps) {
    const organizationScopedRoleAssignments: RoleAssignmentPermissionsView[] = roleAssignments.filter(
        (roleAssignment: RoleAssignmentPermissionsView) =>
            roleAssignment.organization_uuid !== null
    );

    if (!isAdmin || isDeleted || organizationScopedRoleAssignments.length === 0) {
        return null;
    }

    return (
        <PolicyEffectivePermissionsCard
            roleAssignments={organizationScopedRoleAssignments}
        />
    );
}

function formatRoleAssignmentOrganizationCapacities(
    roleAssignment: Pick<
        RoleAssignmentPermissionsView,
        'organization_is_iso' | 'organization_is_merchant' | 'organization_is_customer'
    >
): string {
    const capacities: string[] = [];

    if (roleAssignment.organization_is_iso) {
        capacities.push('ISO');
    }
    if (roleAssignment.organization_is_merchant) {
        capacities.push('MERCHANT');
    }
    if (roleAssignment.organization_is_customer) {
        capacities.push('CUSTOMER');
    }

    return capacities.length > 0 ? capacities.join(', ') : 'NONE';
}

// Helper function to get the back URL based on user type
// function getBackUrl(userType: 'internal' | 'organizational' | 'customer'): string {
//     switch (userType) {
//         case 'internal':
//             return '/internal-users';
//         case 'customer':
//             return '/customer-users';
//         case 'organizational':
//         default:
//             return '/users';
//     }
// }

export function UserDetailContent() {
    const log = createComponentLogger('UserDetailContent');
    const navigate = useNavigate();
    const params = useParams();
    const queryClient = useQueryClient();
    const bumpUserListsVersion: () => void = useDataInvalidationStore(
        (state) => state.bumpUserListsVersion
    );
    const userUuidParam: string | undefined = params.id;
    if (userUuidParam === undefined || userUuidParam.length === 0) {
        throw new Error(`Invalid user UUID: ${params.id}`);
    }

    const [showAssignRole, setShowAssignRole] = useState(false);
    const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
    const [assignedRoleAssignmentUuid, setAssignedRoleAssignmentUuid] = useState<string | null>(
        null
    );
    const [roleToRevoke, setRoleToRevoke] = useState<{
        role_assignment_uuid: string;
        role: Role;
    } | null>(null);
    const [showImpersonateConfirm, setShowImpersonateConfirm] = useState(false);
    const [roleToImpersonate, setRoleToImpersonate] = useState<{
        role_assignment_uuid: string;
        role: Role;
        organization_name: string | null;
    } | null>(null);

    // Edit role assignment state
    const [showEditRole, setShowEditRole] = useState(false);
    const [roleToEdit, setRoleToEdit] = useState<{
        role_assignment_uuid: string;
        role: Role;
        organization_uuid: string | null;
    } | null>(null);

    // Email management state
    const [showAddEmail, setShowAddEmail] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [skipVerificationForNewEmail, setSkipVerificationForNewEmail] = useState(false);
    const [addEmailMessage, setAddEmailMessage] = useState<string | null>(null);
    const [addPhoneMessage, setAddPhoneMessage] = useState<string | null>(null);
    const [emailToRemove, setEmailToRemove] = useState<string | null>(null);
    const [showRemoveEmailConfirm, setShowRemoveEmailConfirm] = useState(false);

    // Password management state
    const [passwordResetRequestMessage, setPasswordResetRequestMessage] = useState<string | null>(
        null
    );
    const [disableMfaMessage, setDisableMfaMessage] = useState<string | null>(null);

    // Delete user confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const { user: currentUser } = useAuthStore();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    // Fetch active role assignment from current session
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!currentUser,
        staleTime: 30000, // Cache for 30 seconds
    });

    const {
        data: user,
        isLoading,
        error,
    } = useQuery<UserProfileDetailedPublicResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
        queryFn: async (): Promise<UserProfileDetailedPublicResponse> => {
            const response = await userApi.getUser(userUuidParam);
            return UserProfileDetailedPublicResponseSchema.parse(response);
        },
    });

    // Check if user can modify this user
    const userUuid: string | undefined = user?.user_uuid;
    const { data: canModifyUserData } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'can_modify_user',
            userUuid,
        ]),
        queryFn: async () => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for authorization check');
            }
            const response = await authorizationApi.canModifyUser(userUuid);
            return response.can_modify;
        },
        enabled: userUuid !== undefined && userUuid.length > 0,
    });

    const canEditUser = canModifyUserData ?? false;
    const requesterIsSuperadmin: boolean = activeRoleAssignment?.role === Role.SUPERADMIN;

    // Check if user can delete this user
    const { data: canDeleteUserData } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'can_delete_user',
            userUuid,
        ]),
        queryFn: async () => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for authorization check');
            }
            const response = await authorizationApi.canDeleteUser(userUuid);
            return response.can_delete;
        },
        enabled: userUuid !== undefined && userUuid.length > 0,
    });

    const canDeleteUser = canDeleteUserData ?? false;
    const isDeleted = user?.status === 'DELETED';
    const canImpersonateByRoleAssignmentUuid: Record<string, boolean> = useMemo(() => {
        const permissionMap: Record<string, boolean> = {};
        if (user === undefined || isDeleted) {
            return permissionMap;
        }
        for (const roleAssignment of getAllRoleAssignments(user)) {
            permissionMap[roleAssignment.role_assignment_uuid] =
                roleAssignment.can_impersonate === true;
        }
        return permissionMap;
    }, [isDeleted, user]);

    const canEditConfig: boolean = canViewUserConfigurationCard(activeRoleAssignment);

    // For backward compatibility - check if user is pillar admin or higher
    const isAdmin = hasInternalAdminPrivileges(activeRoleAssignment ?? null);

    const organizations: OrganizationListItem[] = [];

    // Determine user type and back URL
    const userType = getUserType(user);

    const updateMutation = useMutation({
        mutationFn: ({
            userUuid,
            data,
        }: {
            userUuid: string;
            data: {
                primary_email?: string;
                status?: Status;
                first_name?: string | null;
                last_name?: string | null;
            };
        }) =>
            userApi.updateUser(userUuid, {
                ...data,
                ...(data.status && { status: data.status }),
            }),
        onSuccess: () => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
        },
    });

    const deleteMutation = useMutation({
        mutationFn: userApi.deleteUser,
        onSuccess: () => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowDeleteConfirm(false);
        },
    });

    const undeleteMutation = useMutation({
        mutationFn: userApi.undeleteUser,
        onSuccess: () => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowDeleteConfirm(false);
        },
    });

    const assignRoleMutation = useMutation({
        mutationFn: userApi.assignRole,
        onSuccess: async (_, variables) => {
            bumpUserListsVersion();
            await queryClient.invalidateQueries({
                queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
            });

            const updatedUser = await queryClient.fetchQuery({
                queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                queryFn: async () => await userApi.getUser(userUuidParam),
            });

            const newRoleAssignment = getAllRoleAssignments(updatedUser).find(
                (roleAssignment: RoleAssignmentPermissionsView) =>
                    roleAssignment.role === variables.role &&
                    (variables.organization_uuid !== null
                        ? roleAssignment.organization_uuid === variables.organization_uuid
                        : roleAssignment.organization_uuid === null)
            );

            if (newRoleAssignment) {
                setAssignedRoleAssignmentUuid(newRoleAssignment.role_assignment_uuid ?? null);
            } else {
                setShowAssignRole(false);
                setAssignedRoleAssignmentUuid(null);
            }
        },
    });

    const revokeRoleMutation = useMutation({
        mutationFn: userApi.revokeRole,
        onSuccess: () => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowRevokeConfirm(false);
            setRoleToRevoke(null);
        },
    });

    const startImpersonationMutation = useMutation({
        mutationFn: authApi.startImpersonation,
        onSuccess: () => {
            setShowImpersonateConfirm(false);
            setRoleToImpersonate(null);
            runInBackground(queryClient.cancelQueries(), (err) => {
                log.error('Error cancelling queries after impersonation start', err);
            });
            runInBackground(queryClient.resetQueries({ predicate: () => true }), (err) => {
                log.error('Error resetting queries after impersonation start', err);
            });
            runInBackground(navigate('/dashboard'), (err) => {
                log.error('Error navigating to dashboard after impersonation start', err);
            });
        },
    });

    const updateRoleAssignmentMutation = useMutation({
        mutationFn: ({
            roleAssignmentUuid,
            data,
        }: {
            roleAssignmentUuid: string;
            data: { user_uuid: string; role?: Role };
        }) => userApi.updateRoleAssignment(roleAssignmentUuid, data),
        onSuccess: () => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowEditRole(false);
            setRoleToEdit(null);
        },
    });

    const setEmailsMutation = useMutation({
        mutationFn: (request: SetUserEmailsRequest) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for email update');
            }
            return userApi.setUserEmailsAdmin(userUuid, request);
        },
        onSuccess: () => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setShowAddEmail(false);
            setNewEmail('');
            setShowRemoveEmailConfirm(false);
            setEmailToRemove(null);
        },
    });
    const setPhonesMutation = useMutation({
        mutationFn: (request: SetUserPhonesRequest) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for phone update');
            }
            return userApi.setUserPhonesAdmin(userUuid, request);
        },
        onSuccess: () => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
        },
    });
    const requestAddEmailVerificationMutation = useMutation({
        mutationFn: (payload: { readonly email: string; readonly skip_verification?: boolean }) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for add-email verification');
            }
            return userApi.requestAddEmailVerificationAdmin(userUuid, payload);
        },
        onSuccess: (response) => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setAddEmailMessage(response.message);
            setShowAddEmail(false);
            setNewEmail('');
            setSkipVerificationForNewEmail(false);
        },
    });
    const requestAddPhoneVerificationMutation = useMutation({
        mutationFn: (payload: { readonly phone: string; readonly skip_verification?: boolean }) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for add-phone verification');
            }
            return userApi.requestAddPhoneVerificationAdmin(userUuid, payload);
        },
        onSuccess: (response) => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setAddPhoneMessage(response.message);
        },
    });
    const verifyAddPhoneMutation = useMutation({
        mutationFn: (payload: { readonly phone: string; readonly code: string }) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for add-phone verification');
            }
            return userApi.verifyAddPhoneAdmin(userUuid, payload);
        },
        onSuccess: (response) => {
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
            setAddPhoneMessage(response.message);
        },
    });

    // Password management mutations
    const resetPasswordMutation = useMutation({
        mutationFn: (newPassword: string) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for password reset');
            }
            return userApi.resetPasswordAdmin(userUuid, newPassword);
        },
        onSuccess: () => {
            setPasswordResetRequestMessage(null);
        },
    });

    const requestPasswordResetEmailMutation = useMutation({
        mutationFn: async (email: string) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for password reset email');
            }
            return await userApi.requestPasswordResetAdmin(userUuid, { email });
        },
        onSuccess: async (response) => {
            setPasswordResetRequestMessage(response.message);
            await invalidateUserSingleUseTokensQuery(
                queryClient,
                activeRoleAssignmentId,
                userUuid
            );
        },
        onError: () => {
            setPasswordResetRequestMessage(null);
        },
    });
    const setMfaRequiredMutation = useMutation({
        mutationFn: async (required: boolean) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for MFA requirement update');
            }
            return await userApi.setMfaRequiredAdmin(userUuid, { required });
        },
        onSuccess: async (response) => {
            setDisableMfaMessage(response.message);
            await queryClient.invalidateQueries({
                queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                    'user',
                    userUuidParam,
                ]),
            });
        },
        onError: () => {
            setDisableMfaMessage(null);
        },
    });

    // Set default role assignment mutation
    const setDefaultRoleAssignmentMutation = useMutation({
        mutationFn: (roleAssignmentUuid: string) => {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user_uuid for default role assignment update');
            }
            return userApi.setDefaultRoleAssignment(userUuid, roleAssignmentUuid);
        },
        onSuccess: () => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
        },
    });

    const handleSetDefaultOrganization = async (organizationUuid: string) => {
        const roleAssignmentUuid: string | null = getOrganizationalRoleAssignmentUuid(
            user,
            organizationUuid
        );
        if (roleAssignmentUuid !== null) {
            await setDefaultRoleAssignmentMutation.mutateAsync(roleAssignmentUuid);
        }
    };

    const basicInfoSaveMutation = useMutation({
        mutationFn: async (data: {
            first_name?: string | null;
            last_name?: string | null;
            primary_email?: string;
            status?: Status;
            emails?: {
                emails: string[];
                primary_email: string;
            };
            phones?: {
                phones: string[];
                primary_phone: string | null;
            };
        }) => {
            // First, handle email changes if any
            if (data.emails) {
                await setEmailsMutation.mutateAsync(data.emails);
            }
            if (data.phones) {
                await setPhonesMutation.mutateAsync(data.phones);
            }

            // Then handle other user updates
            const updates: {
                primary_email?: string;
                status?: Status;
                first_name?: string | null;
                last_name?: string | null;
            } = {};

            if (user) {
                if (data.first_name !== undefined && data.first_name !== (user.first_name ?? '')) {
                    updates.first_name = data.first_name;
                }
                if (data.last_name !== undefined && data.last_name !== (user.last_name ?? '')) {
                    updates.last_name = data.last_name;
                }
                if (data.status && data.status !== user.status) {
                    // data.status is already Status from the form validation
                    updates.status = data.status;
                }
            }

            const hasAnyUpdates: boolean =
                updates.first_name !== undefined ||
                updates.last_name !== undefined ||
                updates.status !== undefined;
            if (hasAnyUpdates) {
                if (userUuid === undefined || userUuid.length === 0) {
                    throw new Error('Missing user_uuid for user update');
                }
                await updateMutation.mutateAsync({ userUuid, data: updates });
            }

            // Invalidate queries to refresh data
            bumpUserListsVersion();
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['user', userUuidParam]),
                }),
                (err) => {
                    log.error('Error invalidating queries:', err);
                }
            );
        },
    });

    const handleBasicInfoSave = (data: {
        first_name?: string | null;
        last_name?: string | null;
        primary_email?: string;
        status?: Status;
        emails?: {
            emails: string[];
            primary_email: string;
        };
        phones?: {
            phones: string[];
            primary_phone: string | null;
        };
    }) => {
        basicInfoSaveMutation.mutate(data);
    };
    const handleRequestAddEmailVerification = async (request: {
        readonly email: string;
        readonly skip_verification?: boolean;
    }): Promise<void> => {
        setAddEmailMessage(null);
        await requestAddEmailVerificationMutation.mutateAsync(request);
    };
    const handleRequestAddPhoneVerification = async (request: {
        readonly phone: string;
        readonly skip_verification?: boolean;
    }): Promise<void> => {
        setAddPhoneMessage(null);
        await requestAddPhoneVerificationMutation.mutateAsync(request);
    };
    const handleVerifyAddPhone = async (request: {
        readonly phone: string;
        readonly code: string;
    }): Promise<void> => {
        setAddPhoneMessage(null);
        await verifyAddPhoneMutation.mutateAsync(request);
    };

    // Available roles depend on both target user type and modal mode:
    // - Assign role: internal targets may receive internal + organizational roles.
    // - Edit role assignment: constrain role set to match the assignment's org context.
    const assignAvailableRoles: Role[] = useMemo((): Role[] => {
        // Base roles derived from target user type.
        const baseRoles: Role[] =
            userType === 'internal'
                ? [...AllInternalRoles, ...AllOrganizationalRoles]
                : AllOrganizationalRoles;

        // SUPERADMIN safety gate (frontend UX): only offer SUPERADMIN if actor is SUPERADMIN.
        const actorRole: Role | null = activeRoleAssignment?.role ?? null;
        if (actorRole === Role.SUPERADMIN) {
            return baseRoles;
        }
        return baseRoles.filter((candidateRole: Role) => candidateRole !== Role.SUPERADMIN);
    }, [userType, activeRoleAssignment]);

    const editAvailableRoles: Role[] = useMemo((): Role[] => {
        if (!roleToEdit) {
            return [];
        }
        if (isInternalRole(roleToEdit.role)) {
            return AllInternalRoles;
        }
        return AllOrganizationalRoles;
    }, [roleToEdit]);

    const assignAvailableOrganizations: OrganizationListItem[] =
        useMemo((): OrganizationListItem[] => {
            // Internal admins can assign organizational roles even to internal targets, so provide org options
            // whenever the actor is an internal admin/higher (same condition we use to fetch orgs).
            return isAdmin ? organizations : [];
        }, [organizations, isAdmin]);

    if (isLoading) {
        return (
            <CardColumn>
                <h1 className="text-3xl font-bold mb-8">User Details</h1>
                <p>Loading user...</p>
            </CardColumn>
        );
    }

    if (error || !user) {
        return (
            <div>
                <h1 className="text-3xl font-bold mb-8">User Details</h1>
                <Card>
                    <CardContent className="pt-6">
                        <p className="text-destructive">
                            {error instanceof Error ? error.message : 'User not found'}
                        </p>
                        <Button
                            onClick={() => {
                                runInBackground(navigate(-1), (err) => {
                                    log.error('Error navigating back:', err);
                                });
                            }}
                            className="mt-4"
                        >
                            Back
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        if (user?.status === 'DELETED') {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user UUID for undelete operation');
            }
            undeleteMutation.mutate(userUuid);
        } else {
            if (userUuid === undefined || userUuid.length === 0) {
                throw new Error('Missing user UUID for delete operation');
            }
            deleteMutation.mutate(userUuid);
        }
    };

    const handleAssignRole = async (data: { role: Role; organization_uuid?: string | null }) => {
        if (userUuid === undefined || userUuid.length === 0) {
            throw new Error('Missing user_uuid for role assignment');
        }
        assignRoleMutation.mutate({
            user_uuids: [userUuid],
            role: data.role,
            organization_uuid: data.organization_uuid ?? null,
        });
    };

    const handleRestoreRole = (data: {
        role: Role;
        organization_uuid: string | null;
    }) => {
        if (userUuid === undefined || userUuid.length === 0) {
            throw new Error('Missing user_uuid for role restore');
        }
        assignRoleMutation.mutate({
            user_uuids: [userUuid],
            role: data.role,
            organization_uuid: data.organization_uuid,
        });
    };

    const handleRevokeRole = () => {
        if (!roleToRevoke) return;
        if (userUuid === undefined || userUuid.length === 0) {
            throw new Error('Missing user_uuid for role revoke');
        }
        if (roleToRevoke.role_assignment_uuid.length === 0) {
            throw new Error('Missing role_assignment_uuid for role revoke');
        }

        revokeRoleMutation.mutate({
            role_assignment_uuid: roleToRevoke.role_assignment_uuid,
            user_uuid: userUuid,
        });
    };

    const openRevokeDialog = (roleAssignmentUuid: string, role: Role) => {
        if (roleAssignmentUuid.length === 0) {
            throw new Error('role_assignment_uuid is required for revoke operations');
        }
        setRoleToRevoke({
            role_assignment_uuid: roleAssignmentUuid,
            role,
        });
        setShowRevokeConfirm(true);
    };

    const openImpersonateDialog = (
        roleAssignmentUuid: string,
        role: Role,
        organizationName: string | null
    ) => {
        setRoleToImpersonate({
            role_assignment_uuid: roleAssignmentUuid,
            role,
            organization_name: organizationName,
        });
        setShowImpersonateConfirm(true);
    };

    const openEditDialog = (ra: {
        role_assignment_uuid: string;
        role: Role;
        organization_uuid: string | null;
    }) => {
        if (ra.role_assignment_uuid.length === 0) {
            throw new Error('role_assignment_uuid is required for edit operations');
        }
        setRoleToEdit(ra);
        setShowEditRole(true);
    };

    const handleEditRole = async (data: { role: Role; organization_uuid?: string | null }) => {
        if (!roleToEdit) return;
        if (userUuid === undefined || userUuid.length === 0) {
            throw new Error('Missing user_uuid for role assignment update');
        }

        const updates: { user_uuid: string; role?: Role } = {
            user_uuid: userUuid,
        };

        if (data.role !== undefined && data.role !== roleToEdit.role) {
            updates.role = data.role;
        }

        // Only call API if there are actual changes
        if (updates.role !== undefined) {
            updateRoleAssignmentMutation.mutate({
                roleAssignmentUuid: roleToEdit.role_assignment_uuid,
                data: updates,
            });
        } else {
            setShowEditRole(false);
            setRoleToEdit(null);
        }
    };

    // Determine back tooltip text based on user type
    const { backUrl, backTooltip } = (() => {
        if (userType === 'internal') {
            return { backUrl: '/internal-users', backTooltip: 'Back to Internal Users' };
        } else if (userType === 'customer') {
            return { backUrl: '/customer-users', backTooltip: 'Back to Customer Users' };
        } else if (userType === 'organizational') {
            return { backUrl: '/users', backTooltip: 'Back to Users' };
        } else {
            return { backUrl: undefined, backTooltip: '' };
        }
    })();
    const roleAssignments: RoleAssignmentPermissionsView[] = getAllRoleAssignments(user);
    const defaultConfigRoleAssignmentUuid: string | null =
        getDefaultConfigRoleAssignmentUuid(user);

    return (
        <CardColumn>
            {/* Floating Back Button */}
            <BackButton href={backUrl} tooltip={backTooltip} />

            <h1 className="text-3xl font-bold mb-8">User Details</h1>
            <BasicInformationCard
                user={user}
                isAdmin={canEditUser && !isDeleted}
                onSave={handleBasicInfoSave}
                onRequestAddEmailVerification={handleRequestAddEmailVerification}
                onRequestAddPhoneVerification={handleRequestAddPhoneVerification}
                onVerifyAddPhone={handleVerifyAddPhone}
                isSuperadmin={requesterIsSuperadmin}
                addEmailRequestMessage={addEmailMessage}
                addEmailRequestError={
                    requestAddEmailVerificationMutation.error instanceof Error
                        ? requestAddEmailVerificationMutation.error
                        : null
                }
                isRequestingAddEmail={requestAddEmailVerificationMutation.isPending}
                addPhoneRequestMessage={addPhoneMessage}
                addPhoneRequestError={
                    requestAddPhoneVerificationMutation.error instanceof Error
                        ? requestAddPhoneVerificationMutation.error
                        : null
                }
                isRequestingAddPhone={requestAddPhoneVerificationMutation.isPending}
                verifyAddPhoneError={
                    verifyAddPhoneMutation.error instanceof Error
                        ? verifyAddPhoneMutation.error
                        : null
                }
                isVerifyingAddPhone={verifyAddPhoneMutation.isPending}
                isLoading={basicInfoSaveMutation.isPending}
                error={basicInfoSaveMutation.error}
            />

            {isAdmin && (
                <SingleUseTokensCard
                    title="User Management Tokens"
                    queryKey={buildUserSingleUseTokensQueryKey(activeRoleAssignmentId, userUuid)}
                    fetchTokens={async (outstandingOnly: boolean) => {
                        if (userUuid === undefined || userUuid.length === 0) {
                            throw new Error('Missing user_uuid for single use token search');
                        }
                        return await userApi.listSingleUseTokensByUserAdmin(userUuid, {
                            outstanding_only: outstandingOnly,
                        });
                    }}
                    expireToken={async (singleUseAuthTokenUuid: string) => {
                        return await userApi.expireSingleUseTokenAdmin(singleUseAuthTokenUuid);
                    }}
                    resendToken={async (singleUseAuthTokenUuid: string) => {
                        return await userApi.resendSingleUseTokenAdmin(singleUseAuthTokenUuid);
                    }}
                    emptyOutstandingMessage="No outstanding user management tokens"
                    emptyHistoricalMessage="No historical user management tokens"
                    showUserColumns={false}
                />
            )}

            {/* Password Card - Only for admins */}
            {isAdmin && !isDeleted && (
                <UserPasswordCard
                    user={user}
                    onSetPassword={(newPassword) => {
                        resetPasswordMutation.mutate(newPassword);
                    }}
                    onRequestPasswordReset={async (email) => {
                        await requestPasswordResetEmailMutation.mutateAsync(email);
                    }}
                    canManageMfaRequired={requesterIsSuperadmin}
                    onSetMfaRequired={async (required) => {
                        await setMfaRequiredMutation.mutateAsync(required);
                    }}
                    isSettingMfaRequired={setMfaRequiredMutation.isPending}
                    mfaRequiredError={
                        setMfaRequiredMutation.error instanceof Error
                            ? setMfaRequiredMutation.error
                            : null
                    }
                    mfaRequiredMessage={disableMfaMessage}
                    isSettingPassword={resetPasswordMutation.isPending}
                    setPasswordError={
                        resetPasswordMutation.error instanceof Error
                            ? resetPasswordMutation.error
                            : null
                    }
                    didSetPasswordSucceed={resetPasswordMutation.isSuccess}
                    isRequestingPasswordReset={requestPasswordResetEmailMutation.isPending}
                    passwordResetRequestError={
                        requestPasswordResetEmailMutation.error instanceof Error
                            ? requestPasswordResetEmailMutation.error
                            : null
                    }
                    passwordResetRequestMessage={passwordResetRequestMessage}
                />
            )}

            {/* Activation URL Card - Only for PENDING users */}
            {user.status === 'PENDING' && user.invitation_token && (
                <ActivationUrlCard
                    invitationToken={user.invitation_token}
                    userUuid={user.user_uuid}
                    email={user.primary_email}
                    userRoles={getAllRoleAssignments(user).map(
                        (roleAssignment) => roleAssignment.role
                    )}
                    title="Activation URL"
                    description="This user is pending activation. Anchorbase Portal sends the activation email automatically on creation; use Resend Email if needed."
                    showToken={true}
                />
            )}

            {/* Default Role Card - Only for organizational users */}
            {(() => {
                const userOrgs = roleAssignments
                    .filter((roleAssignment) => roleAssignment.organization_uuid !== null)
                    .map((roleAssignment) => ({
                        organization_uuid: roleAssignment.organization_uuid!,
                        name: roleAssignment.organization_name || 'Organization',
                        type: formatRoleAssignmentOrganizationCapacities(roleAssignment),
                    }))
                    .filter(
                        (org, index, self) =>
                            index ===
                            self.findIndex((o) => o.organization_uuid === org.organization_uuid)
                    )
                    .sort((a, b) => a.name.localeCompare(b.name));

                const defaultRoleAssignment = user.default_role_assignment ?? null;
                const currentDefaultOrgUuid = defaultRoleAssignment?.organization_uuid ?? null;

                if (userOrgs.length === 0) {
                    return null;
                }

                return (
                    <OrganizationSelectionCard
                        title="Default Role"
                        description="Set the organization that initializes this user's session when they log in."
                        organizations={userOrgs}
                        activeOrganizationUuid={currentDefaultOrgUuid}
                        emptyStateText="No default organization set."
                        onSubmit={handleSetDefaultOrganization}
                    />
                );
            })()}

            <UserRolesCard
                isAdmin={isAdmin}
                isDeleted={isDeleted}
                roleAssignments={roleAssignments}
                canImpersonateByRoleAssignmentUuid={
                    canImpersonateByRoleAssignmentUuid ?? null
                }
                onAssignRole={() => {
                    setShowAssignRole(true);
                }}
                onEditRole={openEditDialog}
                onRevokeRole={openRevokeDialog}
                onRestoreRole={handleRestoreRole}
                onImpersonateRole={openImpersonateDialog}
            />

            <UserPolicyPermissionGrantsSection
                isAdmin={isAdmin}
                isDeleted={isDeleted}
                roleAssignments={roleAssignments}
            />

            <UserEffectivePermissionsSection
                isAdmin={isAdmin}
                isDeleted={isDeleted}
                roleAssignments={roleAssignments}
            />

            {/* Configuration Card */}
            {canEditConfig && user && !isDeleted && (
                <ConfigViewerCard
                    roleAssignments={roleAssignments}
                    activeRoleAssignmentUuid={defaultConfigRoleAssignmentUuid}
                    canEdit={canEditConfig}
                    title="User Configuration"
                    description="View and manage configuration for this user"
                />
            )}

            {/* Delete/Restore User Card - Only for users who can delete */}
            {canDeleteUser && (
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {user.status === 'DELETED' ? 'Restore User' : 'Delete User'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">
                                {user.status === 'DELETED'
                                    ? 'Restore this user account.'
                                    : 'Delete this user account.'}
                            </p>
                            <Button
                                variant={user.status === 'DELETED' ? 'default' : 'destructive'}
                                onClick={handleDelete}
                                disabled={deleteMutation.isPending || undeleteMutation.isPending}
                            >
                                {deleteMutation.isPending || undeleteMutation.isPending
                                    ? user.status === 'DELETED'
                                        ? 'Restoring...'
                                        : 'Deleting...'
                                    : user.status === 'DELETED'
                                      ? 'Undelete User'
                                      : 'Delete User'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Assign Role Dialog - Only for admins */}
            {isAdmin && (
                <RoleAssignmentModal
                    open={showAssignRole}
                    onOpenChange={(open) => {
                        if (!open) {
                            setAssignedRoleAssignmentUuid(null);
                        }
                        setShowAssignRole(open);
                    }}
                    mode="assign"
                    userEmail={user.primary_email}
                    userDisplayName={
                        [user.first_name, user.last_name]
                            .filter(
                                (part): part is string =>
                                    part !== null && part.trim().length > 0
                            )
                            .join(' ') || user.primary_email
                    }
                    availableRoles={assignAvailableRoles}
                    availableOrganizations={assignAvailableOrganizations}
                    organizationSearch={organizationApi.searchOrganizations}
                    onSave={handleAssignRole}
                    isLoading={assignRoleMutation.isPending}
                    error={assignRoleMutation.error}
                    assignedRoleAssignmentUuid={assignedRoleAssignmentUuid}
                    initialRole={user.default_role_assignment?.role || ''}
                />
            )}
            {/* Revoke Role Confirmation Dialog */}
            <Dialog open={showRevokeConfirm} onOpenChange={setShowRevokeConfirm}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Revoke Role</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to revoke the role{' '}
                            <strong>{roleToRevoke?.role}</strong> from {user.primary_email}?
                        </DialogDescription>
                    </DialogHeader>
                    {revokeRoleMutation.error && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {revokeRoleMutation.error instanceof Error
                                    ? revokeRoleMutation.error.message
                                    : 'Failed to revoke role'}
                            </AlertDescription>
                        </Alert>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRevokeConfirm(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleRevokeRole}
                            disabled={revokeRoleMutation.isPending}
                        >
                            {revokeRoleMutation.isPending ? 'Revoking...' : 'Revoke Role'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showImpersonateConfirm}
                onOpenChange={(nextOpenState) => {
                    if (nextOpenState) {
                        setShowImpersonateConfirm(true);
                    }
                }}
            >
                <DialogContent
                    onInteractOutside={(event) => {
                        event.preventDefault();
                    }}
                    onEscapeKeyDown={(event) => {
                        event.preventDefault();
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Start Impersonation</DialogTitle>
                        <DialogDescription>
                            {`Do you want to temporarily assume the ${roleToImpersonate?.role ?? ''} role of user ${
                                user.first_name ?? ''
                            } ${user.last_name ?? ''}`.trim()}
                            {roleToImpersonate?.organization_name !== null &&
                            roleToImpersonate?.organization_name !== undefined
                                ? ` @ ${roleToImpersonate.organization_name}`
                                : ''}
                            ?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowImpersonateConfirm(false);
                                setRoleToImpersonate(null);
                            }}
                            disabled={startImpersonationMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (roleToImpersonate === null) {
                                    return;
                                }
                                startImpersonationMutation.mutate(
                                    roleToImpersonate.role_assignment_uuid
                                );
                            }}
                            disabled={startImpersonationMutation.isPending}
                        >
                            {startImpersonationMutation.isPending
                                ? 'Starting...'
                                : 'Start Impersonating'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Edit Role Assignment Dialog - Only show if not deleted */}
            {!isDeleted && roleToEdit && (
                <RoleAssignmentModal
                    open={showEditRole}
                    onOpenChange={(open) => {
                        if (!open) {
                            setRoleToEdit(null);
                        }
                        setShowEditRole(open);
                    }}
                    mode="edit"
                    roleAssignmentUuid={roleToEdit.role_assignment_uuid}
                    initialRole={roleToEdit.role}
                    initialOrganizationUuid={roleToEdit.organization_uuid}
                    userDisplayName={
                        [user.first_name, user.last_name]
                            .filter(
                                (part): part is string =>
                                    part !== null && part.trim().length > 0
                            )
                            .join(' ') || user.primary_email
                    }
                    availableRoles={editAvailableRoles}
                    availableOrganizations={organizations}
                    organizationSearch={organizationApi.searchOrganizations}
                    onSave={handleEditRole}
                    isLoading={updateRoleAssignmentMutation.isPending}
                    error={updateRoleAssignmentMutation.error}
                />
            )}
            {/* Add Email Dialog */}
            <Dialog
                open={showAddEmail}
                onOpenChange={(nextOpenState) => {
                    setShowAddEmail(nextOpenState);
                    if (nextOpenState) {
                        setAddEmailMessage(null);
                    } else {
                        setSkipVerificationForNewEmail(false);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Email Address</DialogTitle>
                        <DialogDescription>
                            Send a verification email before adding this email address to the user.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="newEmail">Email Address</Label>
                            <Input
                                id="newEmail"
                                type="email"
                                placeholder="user@example.com"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                disabled={requestAddEmailVerificationMutation.isPending}
                            />
                        </div>
                        {requesterIsSuperadmin && (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="skipVerificationForNewEmail"
                                    checked={skipVerificationForNewEmail}
                                    onCheckedChange={(checked) =>
                                        setSkipVerificationForNewEmail(checked === true)
                                    }
                                />
                                <Label htmlFor="skipVerificationForNewEmail">
                                    Skip verification (superadmin)
                                </Label>
                            </div>
                        )}
                        {requestAddEmailVerificationMutation.error && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {requestAddEmailVerificationMutation.error instanceof Error
                                        ? requestAddEmailVerificationMutation.error.message
                                        : 'Failed to send verification email'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {addEmailMessage !== null && (
                            <Alert>
                                <AlertDescription>{addEmailMessage}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAddEmail(false);
                                setNewEmail('');
                                setSkipVerificationForNewEmail(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (!user) {
                                    return;
                                }
                                const trimmedEmail = newEmail.trim();
                                if (!trimmedEmail) {
                                    return;
                                }
                                requestAddEmailVerificationMutation.mutate({
                                    email: trimmedEmail,
                                    skip_verification: requesterIsSuperadmin
                                        ? skipVerificationForNewEmail
                                        : undefined,
                                });
                            }}
                            disabled={requestAddEmailVerificationMutation.isPending || !newEmail.trim()}
                        >
                            {requestAddEmailVerificationMutation.isPending
                                ? skipVerificationForNewEmail
                                    ? 'Adding...'
                                    : 'Sending...'
                                : skipVerificationForNewEmail
                                  ? 'Add Email'
                                  : 'Send Verification Email'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Remove Email Confirmation Dialog */}
            <Dialog open={showRemoveEmailConfirm} onOpenChange={setShowRemoveEmailConfirm}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Remove Email Address</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to remove this email address from the user
                            account?
                        </DialogDescription>
                    </DialogHeader>
                    {emailToRemove && (
                        <div className="py-4">
                            <p className="text-sm">
                                <span className="font-medium">Email:</span> {emailToRemove}
                            </p>
                        </div>
                    )}
                    {setEmailsMutation.error && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {setEmailsMutation.error instanceof Error
                                    ? setEmailsMutation.error.message
                                    : 'Failed to remove email'}
                            </AlertDescription>
                        </Alert>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRemoveEmailConfirm(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (!user || !emailToRemove) {
                                    return;
                                }
                                const updatedEmails = user.emails.filter(
                                    (email: string) => email !== emailToRemove
                                );
                                setEmailsMutation.mutate({
                                    emails: updatedEmails,
                                    primary_email: user.primary_email,
                                });
                            }}
                            disabled={setEmailsMutation.isPending}
                        >
                            {setEmailsMutation.isPending ? 'Removing...' : 'Remove Email'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Delete/Restore User Confirmation Modal */}
            {user && (
                <ConfirmationModal
                    open={showDeleteConfirm}
                    onOpenChange={setShowDeleteConfirm}
                    message={
                        user.status === 'DELETED'
                            ? `Are you sure you want to undelete ${user.primary_email}?`
                            : `Are you sure you want to delete ${user.primary_email}?`
                    }
                    onConfirm={handleConfirmDelete}
                    confirmLabel={user.status === 'DELETED' ? 'Undelete' : 'Delete'}
                    isLoading={deleteMutation.isPending || undeleteMutation.isPending}
                />
            )}
        </CardColumn>
    );
}
